import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents } from 'sutra/tokens/colors'

import { MetricSelectionPopover } from 'features/charts-v2/components/MetricSelectionPopover'
import { type MetricType } from 'features/charts-v2/types'

// Groups the optional sync toggle with the add button on the header's right.
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

interface MetricSelectHeaderProps {
  options: Array<{
    field: MetricType
    alias: string
    filter_type: string
    values: []
  }>
  onSelect: (fields: MetricType[]) => void
  selectedMetrics: MetricType[]
  // Constraint props
  isDisabled?: boolean
  disabledTooltip?: string
  // Optional cross-panel sync toggle, rendered left of the add button.
  syncToggle?: React.ReactNode
}

export const MetricSelectHeader: React.FC<MetricSelectHeaderProps> = ({
  options,
  onSelect,
  selectedMetrics,
  isDisabled = false,
  disabledTooltip,
  syncToggle,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [showPopover, setShowPopover] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const handleMetricSelect = (metrics: MetricType | MetricType[]): void => {
    const metricsArray = Array.isArray(metrics) ? metrics : [metrics]
    onSelect(metricsArray)
  }

  const handleClosePopover = (): void => {
    setShowPopover(false)
    setIsHovered(false)
    setTooltipOpen(false)
  }

  const handleClick = (): void => {
    if (!isDisabled) {
      setShowPopover(!showPopover)
    }
  }

  const tooltipTitle = (
    <div>
      <div>Select metrics to display in your chart.</div>
      {isDisabled && disabledTooltip && (
        <div style={{ marginTop: '4px', color: 'gray' }}>{disabledTooltip}</div>
      )}
    </div>
  )

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '8px',
        position: 'relative',
      }}
    >
      <h2 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Metric</h2>

      <HeaderActions>
        {syncToggle}
        <Tooltip open={tooltipOpen} content={tooltipTitle} side="right">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => {
              setTooltipOpen(false)
              handleClick()
            }}
            onMouseEnter={() => {
              setIsHovered(true)
              if (!showPopover) setTooltipOpen(true)
            }}
            onMouseLeave={() => {
              setIsHovered(false)
              setTooltipOpen(false)
            }}
            style={{
              width: 30,
              height: 30,
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: isDisabled
                ? '#f5f5f5'
                : isHovered || showPopover
                  ? accents.reflectionBlue[100]
                  : '#ffffff',
              color: isDisabled ? '#9ca3af' : '#6b7280',
              fontSize: '18px',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              padding: 0,
            }}
          >
            +
          </button>
        </Tooltip>
      </HeaderActions>

      {/* Metric Selection Popover */}
      {showPopover && !isDisabled && (
        <MetricSelectionPopover
          metricOptions={options}
          onMetricSelect={handleMetricSelect}
          onClose={handleClosePopover}
          getSelectedMetrics={() => selectedMetrics}
          triggerRef={triggerRef as React.RefObject<HTMLElement>}
        />
      )}
    </div>
  )
}
